import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import type { Database } from "@/lib/supabase";
import { MobileSheet } from "./MobileSheet";

type Account = Database["public"]["Tables"]["accounts"]["Row"];

interface MobileAccountSwitchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: Account[];
  currentAccountId?: string;
  loading?: boolean;
  onSwitch: (accountId: string) => Promise<boolean>;
}

/**
 * Lista as contas que o usuário pode operar (agency user ou account user
 * multi-conta). Em caso de sucesso o próprio switchAccount recarrega a página,
 * então aqui só tratamos o estado de "trocando" e a falha.
 */
export const MobileAccountSwitchSheet = ({
  open,
  onOpenChange,
  accounts,
  currentAccountId,
  loading = false,
  onSwitch,
}: MobileAccountSwitchSheetProps) => {
  const { t } = useTranslation("common");
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const handleSelect = async (accountId: string) => {
    if (switchingId) return;

    if (accountId === currentAccountId) {
      onOpenChange(false);
      return;
    }

    setFailed(false);
    setSwitchingId(accountId);

    const success = await onSwitch(accountId);
    if (!success) {
      // Sucesso recarrega a página; só chegamos aqui quando deu errado.
      setFailed(true);
      setSwitchingId(null);
    }
  };

  return (
    <MobileSheet open={open} onOpenChange={onOpenChange} title={t("nav.switchAccount")}>
      {failed && (
        <p className="mb-2 rounded-xl bg-red-500/10 px-3 py-2 text-[13px] text-red-500">
          {t("nav.switchAccountError")}
        </p>
      )}

      {loading && accounts.length === 0 ? (
        <p className="px-3 py-4 text-[14px] text-muted-foreground">{t("nav.loading")}</p>
      ) : (
        <div className="flex flex-col gap-1 pb-2">
          {accounts.map((account) => {
            const isCurrent = account.id === currentAccountId;
            const isSwitching = switchingId === account.id;

            return (
              <button
                key={account.id}
                type="button"
                onClick={() => handleSelect(account.id)}
                disabled={switchingId !== null}
                className="flex min-h-[52px] w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:bg-hover disabled:opacity-60"
              >
                <span
                  className={`min-w-0 flex-1 truncate text-[15px] ${
                    isCurrent ? "font-semibold text-foreground" : "font-medium text-foreground"
                  }`}
                >
                  {account.nome}
                </span>
                {isSwitching ? (
                  <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                ) : (
                  isCurrent && <Check className="h-5 w-5 shrink-0 text-accent-blue" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </MobileSheet>
  );
};
